import React, { useState, useCallback } from 'react';
import { Annotation } from '@/services/annotationService';
import { useAnnotations, useMeasurementOverride } from '@/hooks/useAnnotations';
import { useFloorPlanScale } from '@/hooks/useFloorPlanScale';
import { calculateDistance } from '@/utils/scaleCalculations';
import { wrapTouchEvent } from './touchEventWrapper';
import { getDimensionLabelSizing } from './dimensionLabelSizing';

interface ArrowAnnotationProps {
  annotation: Annotation;
  isSelected: boolean;
  isHovered: boolean;
  scale: number;
  onClick: (e: React.MouseEvent) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onMouseDown: (e: React.MouseEvent) => void;
  onTouchStart?: (e: React.TouchEvent) => void;
  onTouchEnd?: (e: React.TouchEvent) => void;
  onTouchMove?: (e: React.TouchEvent) => void;
  isInteractive: boolean;
  floorPlanId?: string;
}

export function ArrowAnnotation({
  annotation,
  isSelected,
  isHovered,
  scale,
  onClick,
  onMouseEnter,
  onMouseLeave,
  onMouseDown,
  onTouchStart,
  onTouchEnd,
  onTouchMove,
  isInteractive,
  floorPlanId,
}: ArrowAnnotationProps) {
  const [isResizing, setIsResizing] = useState(false);
  const [previewEndpoints, setPreviewEndpoints] = useState<{
    start: { x: number; y: number };
    end: { x: number; y: number };
  } | null>(null);
  const { updateAnnotation } = useAnnotations(annotation.floor_plan_id);
  const measurementOverride = useMeasurementOverride();
  const scaleId = floorPlanId || annotation.floor_plan_id || '';
  const { formatMeasurement: fpFormatMeasurement, hasScale: fpHasScale } =
    useFloorPlanScale(scaleId);
  const formatMeasurement = measurementOverride?.formatMeasurement ?? fpFormatMeasurement;
  const hasScale = measurementOverride?.hasScale ?? fpHasScale;
  const dimensionLabel = getDimensionLabelSizing(scale);

  const { data, style } = annotation;
  const start = previewEndpoints?.start || data.start!;
  const end = previewEndpoints?.end || data.end!;
  const label = data.label;

  // Calculate the stroke width - annotations scale with zoom (no division by scale)
  const strokeWidth = style.strokeWidth || 1;
  const hitAreaWidth = Math.max(20 / scale, (strokeWidth * 8) / scale); // Hit area stays constant screen size

  // Calculate arrow head dimensions - scales with zoom
  const arrowHeadLength = 15;
  const arrowHeadAngle = Math.PI / 6; // 30 degrees

  // Calculate arrow direction
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const angle = Math.atan2(dy, dx);
  // Calculate arrow head points
  const arrowPoint1 = {
    x: end.x - arrowHeadLength * Math.cos(angle - arrowHeadAngle),
    y: end.y - arrowHeadLength * Math.sin(angle - arrowHeadAngle),
  };

  const arrowPoint2 = {
    x: end.x - arrowHeadLength * Math.cos(angle + arrowHeadAngle),
    y: end.y - arrowHeadLength * Math.sin(angle + arrowHeadAngle),
  };

  // Calculate label position (middle of the arrow)
  const labelX = (start.x + end.x) / 2;
  const labelY = (start.y + end.y) / 2;
  const fontSize = style.fontSize || 14; // Scales with zoom

  // Handle resize start
  const handleResizeStart = useCallback(
    (e: React.MouseEvent, handle: 'start' | 'end') => {
      if (!isInteractive) return;
      e.stopPropagation();
      e.preventDefault();
      setIsResizing(true);

      // Get the SVG element once
      const svg =
        (e.target as SVGElement).ownerSVGElement || (e.target as SVGElement).closest('svg');
      if (!svg) return;

      // Get initial mouse position in SVG coordinates
      const pt = svg.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      const initialMousePos = pt.matrixTransform(svg.getScreenCTM()!.inverse());

      // Store the original values from annotation data
      const originalStart = { ...annotation.data.start! };
      const originalEnd = { ...annotation.data.end! };

      // Track the last updated values
      let lastStart = originalStart;
      let lastEnd = originalEnd;

      const handleMove = (clientX: number, clientY: number) => {
        const pt = svg.createSVGPoint();
        pt.x = clientX;
        pt.y = clientY;
        const currentMousePos = pt.matrixTransform(svg.getScreenCTM()!.inverse());

        // Calculate delta from initial mouse position
        const deltaX = currentMousePos.x - initialMousePos.x;
        const deltaY = currentMousePos.y - initialMousePos.y;

        let newStart = originalStart;
        let newEnd = originalEnd;

        if (handle === 'start') {
          newStart = {
            x: originalStart.x + deltaX,
            y: originalStart.y + deltaY,
          };
        } else {
          newEnd = {
            x: originalEnd.x + deltaX,
            y: originalEnd.y + deltaY,
          };
        }

        // Store the last values
        lastStart = newStart;
        lastEnd = newEnd;

        setPreviewEndpoints({ start: newStart, end: newEnd });
      };

      const handleMouseMove = (e: MouseEvent) => {
        e.preventDefault();
        handleMove(e.clientX, e.clientY);
      };

      const handleTouchMove = (e: TouchEvent) => {
        e.preventDefault();
        if (e.touches.length === 1) {
          handleMove(e.touches[0].clientX, e.touches[0].clientY);
        }
      };

      const handleEnd = () => {
        setIsResizing(false);

        // Create final update with history if values changed
        if (
          lastStart.x !== originalStart.x ||
          lastStart.y !== originalStart.y ||
          lastEnd.x !== originalEnd.x ||
          lastEnd.y !== originalEnd.y
        ) {
          updateAnnotation({
            id: annotation.id,
            data: {
              data: {
                ...annotation.data,
                start: lastStart,
                end: lastEnd,
              },
            },
            skipHistory: false, // Create history entry for the final position
          }).finally(() => setPreviewEndpoints(null));
        } else {
          setPreviewEndpoints(null);
        }

        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleEnd);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleEnd);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleEnd);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleEnd);
    },
    [annotation, updateAnnotation, isInteractive],
  );

  return (
    <g data-annotation-id={annotation.id} className={isInteractive ? 'cursor-pointer' : ''}>
      {/* Invisible hit area for easier selection */}
      <line
        x1={start.x}
        y1={start.y}
        x2={end.x}
        y2={end.y}
        stroke="transparent"
        strokeWidth={hitAreaWidth}
        pointerEvents={isInteractive ? 'auto' : 'none'}
        onClick={onClick}
        onMouseDown={isResizing ? undefined : onMouseDown}
        onTouchStart={isResizing ? undefined : onTouchStart}
        onTouchEnd={isResizing ? undefined : onTouchEnd}
        onTouchMove={isResizing ? undefined : onTouchMove}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      />

      {/* Arrow line */}
      <line
        x1={start.x}
        y1={start.y}
        x2={end.x}
        y2={end.y}
        stroke={style.color || '#000000'}
        strokeWidth={strokeWidth}
        strokeOpacity={style.opacity || 1}
        strokeDasharray={style.strokeDasharray}
        strokeLinecap="round"
        pointerEvents="none"
      />

      {/* Arrow head */}
      <path
        d={`M ${end.x} ${end.y} L ${arrowPoint1.x} ${arrowPoint1.y} M ${end.x} ${end.y} L ${arrowPoint2.x} ${arrowPoint2.y}`}
        stroke={style.color || '#000000'}
        strokeWidth={strokeWidth}
        strokeOpacity={style.opacity || 1}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
        pointerEvents="none"
      />

      {/* Optional label */}
      {label && (
        <>
          {/* Label background for better readability */}
          <rect
            x={labelX - label.length * fontSize * 0.3}
            y={labelY - fontSize * 0.7}
            width={label.length * fontSize * 0.6}
            height={fontSize * 1.4}
            fill="white"
            fillOpacity={0.8}
            rx={2}
            pointerEvents="none"
          />

          {/* Label text */}
          <text
            x={labelX}
            y={labelY + fontSize * 0.35}
            textAnchor="middle"
            fill={style.color || '#000000'}
            fontSize={fontSize}
            fontFamily={style.fontFamily || 'Arial, sans-serif'}
            fontWeight={style.fontWeight || 'normal'}
            opacity={style.opacity || 1}
            pointerEvents="none"
            style={{
              userSelect: 'none',
              WebkitUserSelect: 'none',
              MozUserSelect: 'none',
              msUserSelect: 'none',
            }}
          >
            {label}
          </text>
        </>
      )}

      {/* Hover effect - clean highlight */}
      {isHovered && !isSelected && (
        <>
          <line
            x1={start.x}
            y1={start.y}
            x2={end.x}
            y2={end.y}
            stroke="#6366f1"
            strokeWidth={Math.max(4 / scale, strokeWidth + 2 / scale)}
            strokeOpacity={0.5}
            strokeLinecap="round"
            strokeDasharray={`${6 / scale} ${3 / scale}`}
            pointerEvents="none"
          />
          <path
            d={`M ${end.x} ${end.y} L ${arrowPoint1.x} ${arrowPoint1.y} M ${end.x} ${end.y} L ${arrowPoint2.x} ${arrowPoint2.y}`}
            stroke="#6366f1"
            strokeWidth={Math.max(4 / scale, strokeWidth + 2 / scale)}
            strokeOpacity={0.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
            pointerEvents="none"
          />
        </>
      )}

      {/* Selection/hover indicator */}
      {(isSelected || isHovered) && (
        <>
          {/* Resize handles when selected */}
          {isSelected && isInteractive ? (
            <>
              {/* Start point handle */}
              <g>
                {/* Invisible larger hit area for touch */}
                <circle
                  cx={start.x}
                  cy={start.y}
                  r={12 / scale}
                  fill="transparent"
                  pointerEvents="all"
                  style={{ cursor: 'move' }}
                  onMouseDown={e => handleResizeStart(e, 'start')}
                  onTouchStart={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    wrapTouchEvent(e => handleResizeStart(e, 'start'))(e);
                  }}
                />
                {/* Visible handle */}
                <circle
                  cx={start.x}
                  cy={start.y}
                  r={6 / scale}
                  fill="#2563eb"
                  stroke="white"
                  strokeWidth={1 / scale}
                  pointerEvents="none"
                />
              </g>

              {/* End point handle */}
              <g>
                {/* Invisible larger hit area for touch */}
                <circle
                  cx={end.x}
                  cy={end.y}
                  r={12 / scale}
                  fill="transparent"
                  pointerEvents="all"
                  style={{ cursor: 'move' }}
                  onMouseDown={e => handleResizeStart(e, 'end')}
                  onTouchStart={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    wrapTouchEvent(e => handleResizeStart(e, 'end'))(e);
                  }}
                />
                {/* Visible handle */}
                <circle
                  cx={end.x}
                  cy={end.y}
                  r={6 / scale}
                  fill="#2563eb"
                  stroke="white"
                  strokeWidth={1 / scale}
                  pointerEvents="none"
                />
              </g>
            </>
          ) : (
            <>
              {/* End point indicators for hover */}
              <circle
                cx={start.x}
                cy={start.y}
                r={4 / scale}
                fill={isSelected ? '#2563eb' : '#6b7280'}
                fillOpacity={0.8}
                pointerEvents="none"
              />
              <circle
                cx={end.x}
                cy={end.y}
                r={4 / scale}
                fill={isSelected ? '#2563eb' : '#6b7280'}
                fillOpacity={0.8}
                pointerEvents="none"
              />
            </>
          )}

          {/* Selection outline */}
          {isSelected && (
            <line
              x1={start.x}
              y1={start.y}
              x2={end.x}
              y2={end.y}
              stroke="#2563eb"
              strokeWidth={strokeWidth + 2 / scale}
              strokeOpacity={0.3}
              strokeLinecap="round"
              pointerEvents="none"
            />
          )}
        </>
      )}

      {/* Length label when showDimensions is enabled */}
      {data.showDimensions &&
        hasScale &&
        (() => {
          const midX = (start.x + end.x) / 2;
          const midY = (start.y + end.y) / 2;
          const angleDeg = angle * (180 / Math.PI);
          const shouldFlip = angleDeg > 90 || angleDeg < -90;
          const textAngleDeg = shouldFlip ? angleDeg + 180 : angleDeg;
          const distance = calculateDistance(start, end);
          const labelY = midY - dimensionLabel.smallOffset;
          return (
            <text
              x={midX}
              y={labelY}
              fill="#3b82f6"
              fontSize={dimensionLabel.fontSize}
              fontWeight="600"
              textAnchor="middle"
              dominantBaseline="auto"
              transform={`rotate(${textAngleDeg} ${midX} ${labelY})`}
              pointerEvents="none"
              style={{ userSelect: 'none' }}
            >
              {formatMeasurement(distance)}
            </text>
          );
        })()}
    </g>
  );
}
